// KPI strip — Net Value, ROI, Break-even computed by the copied financial lib
// over the loaded cost/savings line items.

import { useMemo } from 'react';
import type { PortalCostItem, PortalSavingsItem } from '../../api/types';
import { computeProductKPIs } from '../../lib/financial/product-net-value';
import { toCostBucket, toSavingsBucket } from './helpers';

interface KpiStripProps {
  costs: PortalCostItem[];
  savings: PortalSavingsItem[];
  formatCurrency: (value: number | null | undefined) => string;
}

function formatRoi(roi: number): string {
  if (!Number.isFinite(roi)) return roi > 0 ? '∞' : '—';
  return `${Math.round(roi)}%`;
}

function formatBreakEven(month: number | null): string {
  if (month == null) return '—';
  if (month === 0) return 'Immediate';
  return `${month} mo`;
}

export function KpiStrip({ costs, savings, formatCurrency }: KpiStripProps) {
  const kpis = useMemo(
    () => computeProductKPIs(toCostBucket(costs), toSavingsBucket(savings)),
    [costs, savings],
  );

  const cards: Array<{ label: string; value: string }> = [
    { label: 'Net value (36 mo)', value: formatCurrency(kpis.netValue) },
    { label: 'ROI', value: formatRoi(kpis.roi) },
    { label: 'Break-even', value: formatBreakEven(kpis.breakEvenMonth) },
  ];

  return (
    <div className="grid grid-cols-3 gap-2.5" role="group" aria-label="Business case KPIs">
      {cards.map((c) => (
        <div key={c.label} className="rounded-[10px] border border-line bg-panel px-3 py-2.5">
          <div className="text-[0.66rem] font-bold uppercase tracking-wide text-muted">
            {c.label}
          </div>
          <div className="mt-0.5 text-base font-bold text-ink">{c.value}</div>
        </div>
      ))}
    </div>
  );
}
